import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles } from 'tss-react/mui';
import { Close as CloseIcon, ExitToApp as ExitToAppIcon, Menu as MenuIcon, Person as PersonIcon, Storefront as StorefrontIcon } from '@mui/icons-material';
import { AppBar, Box, Container, Divider, Drawer, IconButton, List, Paper, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import SidebarListItems from './sidebar_items/sidebar_list_items';
import Copyright from './component/copyright';
import ColorModeToggle from './component/color_mode_toggle';
import { getTokenState, invalidateToken } from '../../features/authorization/get_token';
import useNotification from '../../core/hooks/use_notification';
import { deleteTokenNotificationData } from '../../core/utils/notification_utils';
import { DirtyFormContext } from '../../core/context/dirty_form_context';
import { Button } from '../common/button';

const SignedInLayout = ({ children }) => {
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const getToken = useSelector(getTokenState);

    const [open, setOpen] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [logoutModalOpen, setLogoutModalOpen] = useState(false);

    const username = getToken?.data?.userName || 'Administrator';

    const performLogout = () => {
        setLogoutModalOpen(false);
        dispatch(invalidateToken());
        showNotification(deleteTokenNotificationData());
    };

    const handleLogoutClick = () => {
        if (isDirty) {
            setLogoutModalOpen(true);
        } else {
            performLogout();
        }
    };

    return (
        <DirtyFormContext.Provider value={ { isDirty, setIsDirty } }>
            <div className={ cx(classes.root) }>
                <AppBar position="absolute" className={ cx(classes.appBar, open && classes.appBarShift) }>
                    <Toolbar className={ cx(classes.toolbar) }>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={ () => setOpen(true) }
                            className={ cx(classes.menuButton, open && classes.menuButtonHidden) }
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Typography component="h1" variant="h6" color="inherit" noWrap className={ cx(classes.title) }>
                            Store Web Panel
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={ 1 } sx={ {
                            mr: 1,
                            backgroundColor: 'action.hover',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2
                        } }>
                            <PersonIcon fontSize="small"/>
                            <Typography variant="body2" sx={ { fontWeight: 600, letterSpacing: '0.5px' } }>
                                { username }
                            </Typography>
                        </Stack>

                        <ColorModeToggle/>

                        <Tooltip title="Wyloguj">
                            <IconButton color="inherit" onClick={ handleLogoutClick }>
                                <ExitToAppIcon/>
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                <Drawer
                    variant="permanent"
                    classes={ {
                        paper: cx(classes.drawerPaper, !open && classes.drawerPaperClose),
                    } }
                    open={ open }
                >
                    <div className={ cx(classes.toolbarIcon) }>
                        { open && (
                            <Stack direction="row" alignItems="center" spacing={ 1 } sx={ { pl: 1 } }>
                                <Box sx={ { width: 32, height: 32, borderRadius: 2, display: 'grid', placeItems: 'center', background: theme => `linear-gradient(135deg, ${ theme.palette.primary.light }, ${ theme.palette.primary.dark })`, color: 'common.white' } }>
                                    <StorefrontIcon fontSize="small"/>
                                </Box>
                                <Box>
                                    <Typography sx={ { fontWeight: 700, lineHeight: 1.1, color: 'text.primary' } }>Freshpod</Typography>
                                    <Typography variant="caption" color="text.secondary">Panel sklepu</Typography>
                                </Box>
                            </Stack>
                        ) }
                        <IconButton onClick={ () => setOpen(false) }>
                            <CloseIcon/>
                        </IconButton>
                    </div>
                    <Divider/>
                    <List
                        sx={ {
                            maxHeight: '92vh',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            // collapsed drawer shows icons only
                            ...(!open && {
                                '& .MuiListItemText-root, & .MuiListItemButton-root > .MuiSvgIcon-root': { display: 'none' },
                                '& .MuiListItemButton-root': { pl: 2 }
                            })
                        } }>
                        <SidebarListItems/>
                    </List>
                </Drawer>
                <main className={ cx(classes.content) }>
                    <div className={ cx(classes.appBarSpacer) }/>
                    <Container maxWidth="xl" className={ cx(classes.container) }>
                        <Paper className={ cx(classes.paper) } elevation={ 0 }>
                            { children }
                        </Paper>

                        <Box pt={ 4 }>
                            <Copyright/>
                        </Box>
                    </Container>
                </main>

                <Dialog open={ logoutModalOpen } onClose={ () => setLogoutModalOpen(false) } PaperProps={ { sx: { borderRadius: 2 } } } maxWidth={ 'xs' }>
                    <DialogTitle sx={ { pb: 2, fontWeight: 600 } }>
                        Masz niezapisane zmiany.
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Czy na pewno chcesz opuścić tę stronę? Niezapisane dane zostaną utracone.
                        </DialogContentText>
                    </DialogContent>

                    <Divider/>

                    <DialogActions sx={ { p: 2, gap: 1 } }>
                        <Button variant="outlined" onClick={ () => setLogoutModalOpen(false) }>
                            Zostań
                        </Button>
                        <Button onClick={ performLogout }>
                            Opuść bez zapisu
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </DirtyFormContext.Provider>
    );
};

const drawerWidth = 300;

const useStyles = makeStyles()((theme) => ({
    root: {
        display: 'flex',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${ drawerWidth }px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    toolbar: {
        paddingRight: 24,
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
        fontWeight: 600
    },
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
        },
    },
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
    },
    appBarSpacer: theme.mixins.toolbar,
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 64px)',
    },
    paper: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
        border: `1px solid ${ theme.palette.divider }`,
    },
}));

export default SignedInLayout;